import {
  Badge,
  Box,
  FieldSwitch,
  FlexBox,
  Icon,
  IconButton,
  Skeleton,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useRef } from 'react';

import { AwayStatus, presenceApi } from 'apis/presence';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import {
  useOrderedDragDrop,
  UseOrderedDragDropOptions,
} from 'utils/useOrderedDragDrop';

import styles from './awayStatusRow.module.scss';

const type = Symbol('awayStatus');

type AwayStatusRowProps = Pick<UseOrderedDragDropOptions, 'onMove'> & {
  index: number;
  awayStatus: AwayStatus;
  isDragDisabled: boolean;
  isLoading: boolean;
  onActiveStateChange: (id: string) => void;
};

export const AwayStatusRow = ({
  awayStatus,
  index,
  isDragDisabled,
  isLoading,
  onActiveStateChange,
  onMove,
}: AwayStatusRowProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const row = useRef<HTMLTableRowElement>(null);
  const rowHandle = useRef<HTMLDivElement>(null);
  const { isDragging, isOver } = useOrderedDragDrop({
    type,
    index,
    source: rowHandle,
    target: row,
    preview: row,
    onMove,
    triggerSwap: 'drop',
  });

  const [updateAwayStatus, updateAwayStatusResult] =
    presenceApi.useUpdateAwayStatusMutation();

  const handleToggle = async () => {
    const response = await updateAwayStatus({
      id: awayStatus.id,
      is_deactivated: !awayStatus.is_deactivated,
    });

    if (isResponseWithData(response)) {
      onActiveStateChange(awayStatus.id);

      if (awayStatus.is_deactivated) {
        ampli.settingsCustomAwayStatusUnarchived();
      } else {
        ampli.settingsCustomAwayStatusArchived();
      }
    } else {
      notify({
        title: awayStatus.is_deactivated
          ? 'Could not activate away status'
          : 'Could not deactivate away status',
        text: awayStatus.is_deactivated
          ? 'An error occurred while trying to activate the away status'
          : 'An error occurred while trying to deactivate the away status',
        type: 'error',
        icon: 'exclamation',
        status: 'danger',
      });
    }
  };

  const handleEdit = () => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:awayStatus',
          arg: { awayStatus },
        },
      }),
    );
  };

  const dragHandleClassNames = classNames({
    [styles.handle]: true,
    [styles.hide]: isDragDisabled,
  });

  const rowCellClassNames = classNames({
    [styles.cellOver]: isOver,
  });

  return (
    <Table.Tr ref={row} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Table.Td className={rowCellClassNames}>
        <FlexBox gap="300" alignItems="center">
          <div ref={rowHandle} className={dragHandleClassNames}>
            <Icon name="bars" />
          </div>
          <Box>
            {isLoading ? (
              <Skeleton height="14px" width="128px" />
            ) : (
              <Badge size="medium" variant="warning">
                <FlexBox gap="200">
                  <span className={styles.emoji}>{awayStatus.emoji}</span>
                  {awayStatus.label}
                </FlexBox>
              </Badge>
            )}
          </Box>
        </FlexBox>
      </Table.Td>

      <Table.Td className={rowCellClassNames} style={{ width: 0 }}>
        <FieldSwitch
          input={{
            name: 'active',
            value: 'active',
            checked: !awayStatus.is_deactivated,
            onChange: handleToggle,
            disabled: updateAwayStatusResult.isLoading,
          }}
        />
      </Table.Td>

      <Table.Td className={rowCellClassNames} style={{ width: 0 }}>
        <Tooltip content="Edit" dark>
          <IconButton icon="pen" variant="tertiary" onClick={handleEdit} />
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
};
